import React, { useEffect, useId, useRef, useState } from 'react'

interface CardAccordionProps {
  title: string
  includeSnippet?: boolean
}

export const CardAccordion = ({
  title,
  includeSnippet = false,
  children
}: React.PropsWithChildren<CardAccordionProps>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [height, setHeight] = useState<number>(0)
  const [snippetText, setSnippetText] = useState<string>('')
  const contentRef = useRef<HTMLDivElement>(null)
  const generatedId = useId()

  const accordionId = `accordion-${generatedId}`
  const headingId = `${accordionId}-heading`
  const panelId = `${accordionId}-panel`

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0)

      // Extract first section content when includeSnippet is true and accordion is closed
      if (includeSnippet && !isOpen && snippetText === '') {
        const firstChild = contentRef.current.firstElementChild
        if (firstChild) {
          const textContent = firstChild.textContent || ''
          setSnippetText(textContent.trim())
        }
      }
    }
  }, [isOpen, children, includeSnippet, snippetText])

  const toggleAccordion = () => {
    setIsOpen(!isOpen)
  }

  return (
    <div className="card-accordion">
      <button
        className="card-accordion-trigger"
        onClick={toggleAccordion}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-describedby={headingId}
      >
        <span className="card-accordion-trigger-text"><span className="card-accordion-trigger-title">{title}</span>{includeSnippet && !isOpen && <span className="card-accordion-trigger-snippet">{snippetText}</span>}</span>
        <span
          className={`card-accordion-trigger-icon ${isOpen ? 'open' : ''}`}
          aria-hidden="true"
        >
          <svg className="svg-icon" aria-hidden="true" focusable="false">
            <use xmlnsXlink="ttp://www.w3.org/2000/xlink" xlinkHref="#icon-chevron-down" />
          </svg>
        </span>
      </button>
      <div
        id={panelId}
        className="card-accordion-panel"
        style={{ height: `${height}px` }}
        aria-labelledby={headingId}
        aria-hidden={!isOpen}
        role="region"
      >
        <div ref={contentRef} className="card-accordion-panel-inner">
          {children}
        </div>
      </div>
    </div>
  )

}